import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React from 'react'

import Button from '@/components/buttons/Button/Button'
import DefaultInput from '@/components/inputs/DefaultInput/DefaultInput'
import TyreSizePicker, { TyreSizeCriteria } from '@/components/search/TyreSizePicker/TyreSizePicker'
import LicensePlate from '@/components/vehicle/LicensePlate/LicensePlate'
import VehicleSelector from '@/components/vehicle/VehicleSelector/VehicleSelector'
import { resetVehicle, selectLicensePlateValid } from '@/features/vehicle/vehicleSlice'
import { SeoSearchCategory, useSeoSearchCategory } from '@/lib/seo/searchCategory'
import { filterObject } from '@/utils/helpers'
import { useAppDispatch } from '@/utils/hooks/useAppDispatch'
import { useAppSelector } from '@/utils/hooks/useAppSelector'
import useLocalizedPath from '@/utils/hooks/useLocalizedPath'
import { PLATE_SEARCH_FLAG } from '@/utils/hooks/usePlateSearchArrival'
import styles from './SeoCategorySearch.module.scss'

/**
 * Hands the criteria over to the category search. The parameter names are the ones
 * WheelSearch, RimSearch and TireSearch already read back out of the url on arrival, so
 * the reader lands on a page that has run their search rather than an empty one.
 */
const useHandoff = (category: SeoSearchCategory) => {
  const router = useRouter()
  const getLocalizedPath = useLocalizedPath()
  const href = getLocalizedPath(category)

  return (criteria: Record<string, string | undefined>) => {
    const query = new URLSearchParams(filterObject(criteria) as Record<string, string>)
    router.push(query.toString() ? `${href}?${query}` : href)
  }
}

type SearchRowProps = {
  /** Translation key for the free text field. Omitted where the page has no such field. */
  placeholderKey?: string
  description: string
  onDescriptionChange: (value: string) => void
  onReset: () => void
}

/** Free text field and the two buttons, shared by all three forms. */
const SearchRow = ({ placeholderKey, description, onDescriptionChange, onReset }: SearchRowProps) => {
  const { t } = useTranslation('common')

  return (
    <div className={classNames(styles.searchInput, { [styles.buttonsOnly]: !placeholderKey })}>
      {placeholderKey && (
        <DefaultInput
          searchIcon
          name="description"
          placeholder={t(placeholderKey) || ''}
          onChange={onDescriptionChange}
          value={description}
          inputStyle="lightBackground"
        />
      )}
      <Button label={t('search.searchButton') || ''} rounded type="submit" />
      <Button label={t('reset') || ''} dark rounded onClick={onReset} />
    </div>
  )
}

/**
 * Wheels and rims, which both search on a vehicle. Rims additionally take free text —
 * the only difference between the two forms.
 */
const VehicleCriteriaSearch = ({ category }: { category: 'wheels' | 'rims' }) => {
  const dispatch = useAppDispatch()
  const licensePlateValid = useAppSelector(selectLicensePlateValid)
  const [description, setDescription] = React.useState('')
  const handoff = useHandoff(category)

  const takesDescription = category === 'rims'

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    // The plate travels in the store rather than the url, which keeps the number out of
    // browser history and referrer headers; the flag tells the category search to run it.
    // It rules the free text out rather than joining it, so the arriving page runs one
    // search and not two.
    if (licensePlateValid) {
      handoff({ [PLATE_SEARCH_FLAG]: 'true' })
      return
    }

    if (takesDescription) handoff({ description })
  }

  const onReset = () => {
    dispatch(resetVehicle())
    setDescription('')
  }

  return (
    <div className={styles.component}>
      <form className={styles.criteria} onSubmit={onSubmit}>
        <LicensePlate />
        <SearchRow
          placeholderKey={takesDescription ? 'search.searchPlaceholderRim' : undefined}
          description={description}
          onDescriptionChange={setDescription}
          onReset={onReset}
        />
      </form>
      <VehicleSelector onSelectVehicle={(kTypeId) => handoff({ ktype: kTypeId.toString() })} />
    </div>
  )
}

const EMPTY_TYRE_CRITERIA: TyreSizeCriteria = { tyreType: 0 }

const TyreCriteriaSearch = () => {
  const [criteria, setCriteria] = React.useState<TyreSizeCriteria>(EMPTY_TYRE_CRITERIA)
  const [description, setDescription] = React.useState('')
  const handoff = useHandoff('tyres')

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    handoff({
      tyretype: criteria.tyreType ? criteria.tyreType.toString() : undefined,
      width: criteria.width?.toString(),
      aspectRatio: criteria.aspectRatio?.toString(),
      diameter: criteria.diameter?.toString(),
      description,
    })
  }

  const onReset = () => {
    setCriteria(EMPTY_TYRE_CRITERIA)
    setDescription('')
  }

  return (
    <form className={classNames(styles.component, styles.tyreCriteria)} onSubmit={onSubmit}>
      <TyreSizePicker
        tyreType={criteria.tyreType}
        width={criteria.width}
        aspectRatio={criteria.aspectRatio}
        diameter={criteria.diameter}
        onCriteriaChange={setCriteria}
      />
      <SearchRow
        placeholderKey="search.searchPlaceholderTyre"
        description={description}
        onDescriptionChange={setDescription}
        onReset={onReset}
      />
    </form>
  )
}

/**
 * The category search an SEO page opens with, sitting between the page heading and the
 * copy. Which one — or none — comes from the page url; see lib/seo/searchCategory.
 *
 * The criteria are handed over to the category page rather than searched here: these pages
 * are static copy, and holding a result set would give them their own search state.
 */
const SeoCategorySearch = () => {
  const category = useSeoSearchCategory()

  if (!category) return null

  return category === 'tyres' ? <TyreCriteriaSearch /> : <VehicleCriteriaSearch category={category} />
}

export default SeoCategorySearch
